import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { QUEENS_PHOTO, QUEENS_PHOTO_MOBILE } from '../data/photos';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { Tilt3D } from './Tilt3D';

/**
 * The shared photo of both Queens, art-directed per breakpoint.
 *
 * MOBILE (<768px): a vertical 4:5 personal-brand portrait. The source asset is a
 * promotional poster, so it is deliberately cropped hard — the embedded title,
 * tagline, service list, and icons are pushed out of frame. The women, not the
 * poster, are the conversion asset; the page already sells the services in HTML.
 * A dedicated clean mobile photo (QUEENS_PHOTO_MOBILE) replaces the crop when set.
 *
 * DESKTOP (>=768px): the full square composition, where there is room to read it.
 */
export function QueensPhoto() {
  const {
    t
  } = useLanguage();
  if (!QUEENS_PHOTO && !QUEENS_PHOTO_MOBILE) {
    return <div className="aspect-[4/5] w-full overflow-hidden bg-offwhite md:aspect-square" data-id="element-431" data-mp="2k7gmzm5ycawb">
        <PhotoPlaceholder label={t('queens.photo.wide')} tone="warm" data-id="element-432" data-mp="2x36nkfw8wade" />
      </div>;
  }
  const usingCleanMobile = Boolean(QUEENS_PHOTO_MOBILE);
  return <>
      {/* Mobile: tight vertical crop on the two women */}
      <figure className="md:hidden" data-id="element-433" data-mp="2ndvegt9cz5pi">
        <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[10px] bg-offwhite" data-id="element-434" data-mp="2nar3nd1e1bpv">
          <img src={QUEENS_PHOTO_MOBILE || QUEENS_PHOTO} alt={t('queens.alt.both')} loading="lazy" decoding="async" style={{
          objectPosition: usingCleanMobile ? '50% 35%' : '50% 68%'
        }} className={`h-full w-full object-cover ${usingCleanMobile ? '' : 'scale-[1.38]'}`} data-id="element-435" data-mp="2ic64lgxvgy30" />
          {/* Bottom-only scrim, present solely so the small label stays legible. */}
          <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-ink/75 to-transparent" data-id="element-436" data-mp="1uoohvztgsq6w" />
          <figcaption className="absolute inset-x-0 bottom-0 p-4" data-id="element-437" data-mp="1qp7ct7hy9v29">
            <span className="block font-display text-sm font-extrabold uppercase tracking-[0.16em] text-white" data-id="element-438" data-mp="1s4jhaw3z0n7n">
              {t('footer.people')}
            </span>
            <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70" data-id="element-439" data-mp="1i6lw6hher6j9">
              Miami Car Queens
            </span>
          </figcaption>
        </div>
      </figure>

      {/* Desktop: the clean vertical when available, otherwise the poster composition. */}
      <figure className="hidden md:block" data-id="element-440" data-mp="1e4h25hn1s7w3">
        <Tilt3D max={5} lift={12} data-id="element-441" data-mp="1e6bpt3ppee3v">
          <div className={`w-full overflow-hidden bg-offwhite shadow-[0_30px_60px_-40px_rgba(5,20,31,0.7)] ${usingCleanMobile ? 'aspect-[4/5]' : 'aspect-square'}`} data-id="element-442" data-mp="zfkv9nwq90x2">
            <img src={QUEENS_PHOTO_MOBILE || QUEENS_PHOTO} alt={t('queens.alt.both')} loading="lazy" decoding="async" style={usingCleanMobile ? {
            objectPosition: '50% 22%'
          } : undefined} className="h-full w-full object-cover object-center" data-id="element-443" data-mp="qeef5fxzps4c" />
          </div>
        </Tilt3D>
        <figcaption className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-camelink" data-id="element-444" data-mp="ksma2aogr2qs">
          {t('queens.caption')}
        </figcaption>
      </figure>
    </>;
}